import { ApolloError } from '@apollo/client'

const defaultErrorMessage = `Oops! Looks like something went wrong. Please try again or contact support.` // errorCode

// Backend error messages are formatted as "{code}: {message}" (e.g. "400_User_043: Security verification is required.")
// Strip the code prefix so users only see the human-readable message.
const errorCodePrefix = /^\d{3}_\w+_\d+:\s*/

function stripErrorCode(message: string): string {
	return message.replace(errorCodePrefix, '')
}

function messageOrDefault(message: string | undefined): string {
	return message ? stripErrorCode(message) : defaultErrorMessage
}

// What the server sends for one error, whether Apollo surfaces it in `graphQLErrors` or inside a 4xx
// `networkError.result` — neither shape is typed beyond these fields.
interface ServerError {
	message?: unknown
	extensions?: { error_code?: unknown; message?: unknown } | null
}

function networkServerErrors(error: ApolloError): ServerError[] {
	const errors = (error.networkError as { result?: { errors?: unknown } } | null)?.result?.errors
	return Array.isArray(errors) ? (errors as ServerError[]) : []
}

// The BE can return an ETag conflict even when the update succeeded (stale grain state race condition).
// Apollo surfaces this via networkError (data: null response), not graphQLErrors.
// Callers should suppress the error and treat the operation as successful.
export function isETagConflictError(error: ApolloError): boolean {
	return networkServerErrors(error).some(e => {
		const code = e.extensions?.error_code
		return typeof code === 'string' && code.includes('ETag')
	})
}

// Contact list errors (filter validation, attendee permission, too many ticket holders) carry their
// user-facing copy in `extensions.message`. They can arrive as GraphQL errors or, on a 4xx response,
// inside the network error's result.
const contactListErrorCode = /^\d{3}_ContactList_\d+$/

function contactListErrorMessage(error: ApolloError): string | undefined {
	const errors: ServerError[] = [...(error.graphQLErrors ?? []), ...networkServerErrors(error)]
	for (const e of errors) {
		const code = e.extensions?.error_code
		const message = e.extensions?.message
		if (typeof code === 'string' && contactListErrorCode.test(code) && typeof message === 'string' && message) {
			return message
		}
	}
	return undefined
}

export function trueErrorMessage(error: string | ApolloError | unknown) {
	console.error('Unexpected error', error)
	if (error instanceof ApolloError) {
		const contactListMessage = contactListErrorMessage(error)
		if (contactListMessage) {
			return contactListMessage
		}

		const networkMessage = networkServerErrors(error)[0]?.message
		if (typeof networkMessage === 'string' && networkMessage) {
			return stripErrorCode(networkMessage)
		}

		return messageOrDefault(error.message)
	}

	if (typeof error === 'object' && error !== null && 'message' in error) {
		return messageOrDefault((error as any).message)
	}

	if (typeof error === 'string') {
		return messageOrDefault(error)
	}

	return messageOrDefault(undefined)
}

// A "you may not see this" answer: the 403 family of backend codes ("403_Event_000"), or the GraphQL
// authorization code. Callers hide what the user cannot see rather than show it as a failure.
export function isForbiddenError(error: unknown): boolean {
	if (!(error instanceof ApolloError)) {
		return false
	}
	const errors: ServerError[] = [...(error.graphQLErrors ?? []), ...networkServerErrors(error)]
	return errors.some(e => {
		const code = e.extensions?.error_code
		const graphQlCode = (e.extensions as { code?: unknown } | null | undefined)?.code
		return (typeof code === 'string' && code.startsWith('403')) || graphQlCode === 'AUTH_NOT_AUTHORIZED'
	})
}
